import {
  CustomerField,
  SignUpInput,
  MaskedProperty,
} from '@jetshop/core/types';
import { SignUpFormInput } from '@jetshop/core/components/Auth/formatSignupInput';
import { Form, Formik, FormikProps } from 'formik';
import React, { useContext, useMemo } from 'react';
import { createValidatorForFields } from '@jetshop/core/components/Auth/formFieldsFromSettings';
import SignupProvider, {
  SignupContext,
  SignupFormikContext,
} from '@jetshop/core/components/Auth/signup-context';
import { useSignupSubmit } from './signupSubmit';
import { isUnchangedMaskedValue } from '../Signup/isUnchangedMaskedValue';

type LookupData = {
  [key: string]: MaskedProperty;
} & { externalId?: string | number };

interface SignupFormProps {
  children(args: { formik: FormikProps<SignUpInput> }): React.ReactChild;
  validationSchema?: any;
  lookupData?: LookupData;
  additionalInitialValues?: Partial<SignUpFormInput>;
  additionalFormatter?: (values: SignUpFormInput) => SignUpFormInput;
  setUserExists?: (key: string | null) => void;
  specialValidation?: (values: SignUpFormInput) => any;
}

function getFieldDefaultValues(
  fields: CustomerField[],
  lookupData?: LookupData
) {
  const values: any = {};
  fields.forEach((field) => {
    values[field.name] = lookupData?.[field.name]?.masked ?? '';
  });
  return values;
}

function stripMaskedFieldErrors(
  errors: Record<string, string>,
  values: SignUpFormInput,
  lookupData?: LookupData
) {
  if (!lookupData) return errors;
  const next = { ...errors };
  Object.keys(next).forEach((key) => {
    if (isUnchangedMaskedValue(values[key], lookupData[key]?.masked)) {
      delete next[key];
    }
  });
  return next;
}

function formatLookupData(
  signupValues: SignUpFormInput,
  lookupData?: LookupData
) {
  if (!lookupData) return signupValues;

  const values: any = {
    ...signupValues,
    ...(lookupData.externalId != null
      ? { externalId: String(lookupData.externalId) }
      : {}),
  };

  Object.keys(values).forEach((key) => {
    if (isUnchangedMaskedValue(values[key], lookupData[key]?.masked)) {
      values[key] = lookupData[key]?.encrypted;
    }
  });
  return values;
}

function SignupForm({
  specialValidation,
  additionalFormatter,
  additionalInitialValues,
  ...props
}: SignupFormProps) {
  const lookupData = props.lookupData;
  const { allFields, selectedCountry } = useContext(SignupContext);
  const signupSubmit = useSignupSubmit();
  const fieldValidate = useMemo(
    () => createValidatorForFields(allFields),
    [allFields]
  );

  const initialValues: SignUpInput = useMemo(() => {
    return {
      ...getFieldDefaultValues(allFields, lookupData),
      country: selectedCountry.code,
      consents: {},
      ...additionalInitialValues,
    };
  }, [allFields, lookupData, selectedCountry.code, additionalInitialValues]);

  const validate = (values: SignUpFormInput) => {
    const errors = stripMaskedFieldErrors(
      fieldValidate(values) || {},
      values,
      lookupData
    );
    // Side-effect validators (e.g. uniqueness checks) — do not treat return as errors
    specialValidation?.(values);
    return errors;
  };

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={props.validationSchema}
      validateOnBlur
      validateOnChange={false}
      onSubmit={(dirty_values, actions) => {
        const prepared = additionalFormatter
          ? additionalFormatter(dirty_values)
          : dirty_values;
        signupSubmit({
          values: formatLookupData(prepared, lookupData),
          actions,
        });
      }}
      validate={validate}
      initialValues={initialValues}
    >
      {(formik) => props.children({ formik })}
    </Formik>
  );
}

function SignupFormProvider({
  LoadingComponent,
  setUserExists,
  specialValidation,
  ...props
}: SignupFormProps & { LoadingComponent?: JSX.Element }) {
  return (
    <SignupProvider LoadingComponent={LoadingComponent}>
      <SignupForm
        setUserExists={setUserExists}
        specialValidation={specialValidation}
        {...props}
      >
        {({ formik }) => (
          <Form>
            <SignupFormikContext.Provider value={{ formik }} {...props} />
          </Form>
        )}
      </SignupForm>
    </SignupProvider>
  );
}

export default SignupFormProvider;
